// ============================================================
// 9. TopCustomersReportPage.tsx
// ============================================================
// src/features/reports/pages/TopCustomersReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getTopCustomers, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Users, DollarSign, ShoppingCart, TrendingUp,
    UserCheck, Star, Award, Crown, Phone, Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';

const tierIcons: Record<string, React.ElementType> = {
    platinum: Crown,
    gold: Award,
    silver: Star,
    bronze: UserCheck,
};

const tierColors: Record<string, string> = {
    platinum: 'from-slate-300 to-slate-400 text-slate-800',
    gold: 'from-amber-400 to-yellow-500 text-amber-900',
    silver: 'from-slate-300 to-slate-400 text-slate-700',
    bronze: 'from-amber-600 to-orange-700 text-amber-100',
};

export function TopCustomersReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-top-customers', tenantId, params],
        queryFn: () => getTopCustomers(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'top_customers', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const customers = data?.customers || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            <ReportHeader
                title="Top Customers"
                description="Customer spending analysis"
                icon={Users}
                iconGradient="from-amber-500 to-orange-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || customers.length === 0 ? (
                    <EmptyState
                        icon={<Users className="w-10 h-10" />}
                        title="No Customer Data"
                        description="No customer transactions found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Customers"
                                value={String(data.summary?.totalCustomers || 0)}
                                icon={Users}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Visits"
                                value={formatNumber(data.summary?.totalVisits || 0)}
                                icon={ShoppingCart}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Top Spender"
                                value={data.summary?.topSpender || 'N/A'}
                                icon={Crown}
                                gradient="from-purple-500 to-violet-600"
                            />
                        </div>

                        {/* Top 3 Customers Highlight */}
                        {customers.slice(0, 3).length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {customers.slice(0, 3).map((customer: any, i: number) => {
                                    const TierIcon = tierIcons[customer.loyaltyTier] || UserCheck;
                                    const rankColors = [
                                        'from-amber-400 to-yellow-500 border-amber-300',
                                        'from-slate-300 to-slate-400 border-slate-300',
                                        'from-amber-600 to-orange-700 border-amber-500',
                                    ];

                                    return (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.1 }}
                                        >
                                            <Card className={`border-2 ${rankColors[i]} overflow-hidden`}>
                                                <CardContent className="p-5">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' :
                                                                i === 1 ? 'bg-slate-100 text-slate-700' :
                                                                    'bg-orange-100 text-orange-700'
                                                            }`}>
                                                            #{i + 1}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-bold text-slate-900">{customer.name}</p>
                                                            <div className="flex items-center gap-1 text-xs text-slate-500">
                                                                <Phone size={10} />
                                                                {customer.phone || 'No phone'}
                                                            </div>
                                                        </div>
                                                        <TierIcon className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                    <p className="text-2xl font-bold text-slate-900">
                                                        {formatNaira(customer.totalSpent)}
                                                    </p>
                                                    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                                                        <span>{customer.visits} visits</span>
                                                        <span>Avg: {formatNaira(customer.avgOrderValue)}</span>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}

                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800">All Customers</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase w-12">#</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Phone</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Tier</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Visits</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Total Spent</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Avg Order</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Lifetime</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Last Visit</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {customers.map((customer: any, i: number) => {
                                            const TierIcon = tierIcons[customer.loyaltyTier] || UserCheck;
                                            return (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <span className={`text-sm font-bold ${i < 3 ? 'text-amber-600' : 'text-slate-400'
                                                            }`}>
                                                            {i + 1}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                                                <span className="text-xs font-bold text-slate-600">
                                                                    {customer.name?.charAt(0) || '?'}
                                                                </span>
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{customer.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">{customer.phone}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <TierIcon className="w-3.5 h-3.5" />
                                                            <Badge
                                                                variant={
                                                                    customer.loyaltyTier === 'platinum' ? 'premium' :
                                                                        customer.loyaltyTier === 'gold' ? 'warning' :
                                                                            customer.loyaltyTier === 'silver' ? 'neutral' : 'info'
                                                                }
                                                                size="sm"
                                                            >
                                                                {customer.loyaltyTier}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm font-semibold">
                                                        {customer.visits}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(customer.totalSpent)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(customer.avgOrderValue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <div className="space-y-0.5">
                                                            <p className="text-xs text-slate-500">
                                                                {formatNaira(customer.lifetimeSpent)}
                                                            </p>
                                                            <p className="text-xs text-slate-400">
                                                                {customer.lifetimeVisits} visits
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {customer.lastVisit ? new Date(customer.lastVisit).toLocaleDateString() : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}